import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../_services/api/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '../../_models/api-response.model';
import { RegisterRequest } from '../../_models/auth/register_request.model';
import { Address } from '../../_models/address.model';
import { CustomValidators } from '../../_validators/custom.validators';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm!: FormGroup;
  addressForm!: FormGroup;
  
  currentStep: number = 1;
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  showSuccess: boolean = false;
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    this.addressForm = this.fb.group({
      street: ['', [Validators.maxLength(200)]],
      streetNumber: ['', [Validators.maxLength(200)]],
      city: ['', [Validators.maxLength(100)]],
      postalCode: ['', [Validators.pattern(/^[0-9]{4,10}$/)]],
    });

    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      email: ['', [Validators.required, CustomValidators.email]],
      companyName: ['', [Validators.required]],
      vatNumber: ['', [Validators.required]],
      phoneNumber: ['', [Validators.required, CustomValidators.phone]],
      password: ['', [Validators.required, CustomValidators.password]],
      confirmPassword: ['', [Validators.required]],
      address: this.addressForm
    }, {
      validators: CustomValidators.match('password', 'confirmPassword')
    });
  }

  get firstName() { return this.registerForm.get('firstName'); }
  get lastName() { return this.registerForm.get('lastName'); }
  get email() { return this.registerForm.get('email'); }
  get companyName() { return this.registerForm.get('companyName'); }
  get vatNumber() { return this.registerForm.get('vatNumber'); }
  get phoneNumber() { return this.registerForm.get('phoneNumber'); }
  get password() { return this.registerForm.get('password'); }
  get confirmPassword() { return this.registerForm.get('confirmPassword'); }

  get street() { return this.addressForm.get('street'); }
  get streetNumber() { return this.addressForm.get('streetNumber'); }
  get city() { return this.addressForm.get('city'); }
  get postalCode() { return this.addressForm.get('postalCode'); }

  goToNextStep(): void {
    this.markStep1FieldsTouched();
    
    if (this.isStep1Valid()) {
      this.currentStep = 2;
      this.errorMessage = '';
      if (isPlatformBrowser(this.platformId)) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    } else {
      this.errorMessage = 'Veuillez corriger les erreurs avant de continuer.';
      this.scrollToFirstError();
    }
  }

  goToPreviousStep(): void {
    this.currentStep = 1;
    this.errorMessage = '';
  }

  private markStep1FieldsTouched(): void {
    const step1Fields = ['firstName', 'lastName', 'email', 'phoneNumber', 'companyName', 'vatNumber'];
    
    step1Fields.forEach(field => {
      const control = this.registerForm.get(field);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  isStep1Valid(): boolean {
    const step1Fields = ['firstName', 'lastName', 'email', 'phoneNumber', 'companyName', 'vatNumber'];
    
    return step1Fields.every(field => {
      const control = this.registerForm.get(field);
      return control ? !control.invalid : true;
    });
  }

  onSubmit(): void {
    this.markFormGroupTouched(this.registerForm);
    this.markFormGroupTouched(this.addressForm);
    
    if (this.registerForm.invalid) {
      this.errorMessage = 'Veuillez corriger les erreurs dans le formulaire.';
      this.scrollToFirstError();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.registerForm.value;
    const registerRequest: RegisterRequest = {
      email: formValue.email,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      phoneNumber: formValue.phoneNumber,
      companyName: formValue.companyName,
      vatNumber: formValue.vatNumber,
      password: formValue.password,
      address: formValue.address ? formValue.address as Address : undefined
    };

    this.authService.register(registerRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<void>) => {
          this.isSubmitting = false;
          this.showSuccess = true;
          this.successMessage = response.message || 'Inscription réussie! Veuillez vérifier votre email pour activer votre compte.';
          this.registerForm.reset();
        },
        error: (error: HttpErrorResponse) => {
          this.isSubmitting = false;
          
          if (error.status === 400) {
            this.errorMessage = error.error?.message || 'Erreur de validation. Veuillez vérifier vos informations.';
          } else if (error.status === 409 || error.status === 403) {
            this.errorMessage = 'Un utilisateur avec cet email existe déjà.';
          } else {
            this.errorMessage = 'Une erreur est survenue lors de l\'inscription. Veuillez réessayer.';
          }
          
          this.scrollToFirstError();
        }
      });
  }

  togglePassword(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  resendVerification(): void {
    const email = this.registerForm.get('email')?.value;
    if (email) {
      this.isLoading = true;
      this.authService.resendVerification(email)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.isLoading = false;
            this.successMessage = 'Lien de vérification renvoyé avec succès! Vérifiez votre boîte email.';
          },
          error: (error: HttpErrorResponse) => {
            this.isLoading = false;
            this.errorMessage = 'Erreur lors de l\'envoi du lien de vérification.';
          }
        });
    } else {
      this.errorMessage = 'Impossible de renvoyer le lien. Veuillez vous inscrire à nouveau.';
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private scrollToFirstError(): void {
    if (isPlatformBrowser(this.platformId)) {
      const firstErrorElement = document.querySelector('.form-error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }
}